// src\Pages\Portfolio.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usePortfolio } from '../hooks/usePortfolio';
import {
  FiGrid,
  FiHeart,
  FiAlertCircle,
  FiArrowRight,
  FiImage,
  FiChevronDown,
  FiFilter,
  FiX,
  FiExternalLink,
  FiStar,
  FiBookOpen,
  FiPenTool,
} from 'react-icons/fi';
import { TbBrandFiverr } from 'react-icons/tb';
import { motion, AnimatePresence } from 'framer-motion';
import PortfolioSkeleton from '../components/portfolio/PortfolioSkeleton';
import ReviewsSection from '../components/ReviewsSection';
import StatsSection from '../components/StatsSection';
import { FiverrCard } from '../components/FiverrCard';
import { ogImageService } from '../services/ogImageService';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

// =============================================
// GLASSY DIVIDER
// =============================================
const GlassyDivider = () => {
  return (
    <div className="relative py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="relative h-px w-full overflow-hidden">
          <motion.div
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute inset-0 w-1/3"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--text), transparent)',
              opacity: 0.1,
            }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================
// GLASSY CARD
// =============================================
const GlassyCard = ({ children, className = '' }) => {
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-xl ${className}`}
      style={{
        backgroundColor: 'rgba(var(--bg-rgb), 0.4)',
        border: '1px solid rgba(var(--text-rgb), 0.06)',
        boxShadow:
          '0 8px 32px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
      }}
    >
      {/* Glassy shine effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent" />
        <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
      </div>
      {children}
    </div>
  );
};

// =============================================
// HERO SECTION - Liquid Gradient Animation (Shorter)
// =============================================
const PortfolioHero = () => {
  return (
    <div className="relative mb-8 overflow-hidden rounded-3xl">
      {/* Glassy gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:from-blue-500/10 dark:via-transparent dark:to-purple-500/10" />

      {/* Liquid gradient animated blobs */}
      <motion.div
        animate={{
          x: ['-20%', '15%', '-5%', '20%', '-20%'],
          y: ['-15%', '10%', '-10%', '15%', '-15%'],
          scale: [1, 1.3, 0.9, 1.2, 1],
          borderRadius: [
            '60% 40% 30% 70%',
            '40% 60% 70% 30%',
            '30% 70% 60% 40%',
            '70% 30% 40% 60%',
            '60% 40% 30% 70%',
          ],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-5 left-5 w-72 h-72 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #3b82f6 0%, #60a5fa 40%, transparent 70%)',
          opacity: 0.12,
        }}
      />

      <motion.div
        animate={{
          x: ['15%', '-15%', '10%', '-10%', '15%'],
          y: ['15%', '-10%', '20%', '-15%', '15%'],
          scale: [1.2, 0.9, 1.4, 0.8, 1.2],
          borderRadius: [
            '40% 60% 70% 30%',
            '60% 40% 30% 70%',
            '70% 30% 40% 60%',
            '30% 70% 60% 40%',
            '40% 60% 70% 30%',
          ],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-5 right-5 w-80 h-80 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #8b5cf6 0%, #a78bfa 40%, transparent 70%)',
          opacity: 0.1,
        }}
      />

      <motion.div
        animate={{
          x: ['-5%', '10%', '-10%', '5%', '-5%'],
          y: ['10%', '-15%', '5%', '-10%', '10%'],
          scale: [1, 1.2, 0.8, 1.3, 1],
          borderRadius: [
            '50% 30% 60% 40%',
            '30% 60% 40% 70%',
            '60% 40% 70% 30%',
            '40% 70% 30% 60%',
            '50% 30% 60% 40%',
          ],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #06b6d4 0%, #22d3ee 40%, transparent 70%)',
          opacity: 0.08,
        }}
      />

      <motion.div
        animate={{
          x: ['10%', '-20%', '15%', '-5%', '10%'],
          y: ['-5%', '15%', '-15%', '10%', '-5%'],
          scale: [0.8, 1.4, 0.9, 1.1, 0.8],
          borderRadius: [
            '70% 30% 50% 50%',
            '30% 70% 50% 50%',
            '50% 50% 70% 30%',
            '50% 50% 30% 70%',
            '70% 30% 50% 50%',
          ],
        }}
        transition={{
          duration: 22,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-20 left-20 w-60 h-60 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #ec4899 0%, #f472b6 40%, transparent 70%)',
          opacity: 0.06,
        }}
      />

      {/* Content - Shorter height */}
      <div className="relative pt-34 max-sm:pt-20 pb-20 md:py-16 lg:py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-sm backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.6)',
            border: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <FiBookOpen className="w-4 h-4" style={{ color: '#3b82f6' }} />
          <span style={{ color: 'var(--muted)' }}>Work i served</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3 tracking-tight leading-[1.1]"
          style={{ color: 'var(--text)' }}
        >
          Creative
          <span className="block" style={{ color: 'var(--muted)' }}>
            Design Portfolio
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-sm md:text-base max-w-2xl mx-auto leading-relaxed"
          style={{ color: 'var(--muted)', opacity: 0.8 }}
        >
          A curated collection of my best design work across branding, product
          design, and digital experiences.
        </motion.p>

        {/* Minimal decorative line */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-6 flex items-center justify-center gap-3"
        >
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <span
            className="text-xs tracking-widest uppercase"
            style={{ color: 'var(--muted)', opacity: 0.3 }}
          >
            Explore
          </span>
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
        </motion.div>
      </div>
    </div>
  );
};

// =============================================
// MAIN PORTFOLIO COMPONENT
// =============================================
function Portfolio() {
  const {
    projects,
    allProjects,
    loading,
    error,
    selectedCategory,
    setSelectedCategory,
    selectedType,
    setSelectedType,
    loadMoreProjects,
    hasMore,
    loadingMore,
    trackClick,
  } = usePortfolio();

  const [animatedSections, setAnimatedSections] = useState({});
  const [hoveredId, setHoveredId] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [imagesLoaded, setImagesLoaded] = useState({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // ===== Generate OG Meta Data for Portfolio Homepage =====
  const metaData = useMemo(() => {
    return ogImageService.portfolioHome();
  }, []);

  // Calculate counts based on ALL projects
  const totalProjectsCount = allProjects?.length || 0;
  const brandingCount =
    allProjects?.filter(p => p.category === 'branding').length || 0;
  const productCount =
    allProjects?.filter(p => p.category === 'product').length || 0;
  const othersCount =
    allProjects?.filter(p => p.category === 'others').length || 0;

  const projectCount =
    allProjects?.filter(p => p.type === 'project').length || 0;
  const randomCount = allProjects?.filter(p => p.type === 'random').length || 0;

  const categories = [
    { id: 'all', name: 'All Work', count: totalProjectsCount },
    { id: 'branding', name: 'Brand Identity', count: brandingCount },
    { id: 'product', name: 'Product Design', count: productCount },
    { id: 'others', name: 'Visual Design', count: othersCount },
  ];

  const types = [
    { id: 'all', name: 'All Types', count: totalProjectsCount },
    { id: 'project', name: 'Projects', count: projectCount },
    { id: 'random', name: 'Random Works', count: randomCount },
  ];

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsFilterOpen(false);
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate schema markup for all projects
  const generateSchemaMarkup = () => {
    if (!allProjects || allProjects.length === 0) return null;

    const items = allProjects.slice(0, 10).map((project, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'CreativeWork',
        '@id': `${window.location.origin}/portfolio/${project.id}`,
        name: project.seoTitle || project.title,
        description: project.seoDescription || project.description,
        image: project.thumbnailUrl || project.images?.[0]?.url || '',
        dateCreated: project.createdAt,
        author: {
          '@type': 'Person',
          name: 'William Doornbos',
        },
      },
    }));

    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Portfolio - William Doornbos',
      description:
        'Explore my creative portfolio of branding, product design, and digital experiences.',
      url: window.location.origin + '/portfolio',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: items,
      },
    };
  };

  const schemaMarkup = generateSchemaMarkup();

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleImageError = (projectId, imageIndex = 0) => {
    const key = `${projectId}_${imageIndex}`;
    setImageErrors(prev => ({ ...prev, [key]: true }));
  };

  const handleImageLoad = (projectId, imageIndex = 0) => {
    const key = `${projectId}_${imageIndex}`;
    setImagesLoaded(prev => ({ ...prev, [key]: true }));
  };

  const getCleanCloudinaryUrl = url => {
    if (!url) return url;
    if (!url.includes('cloudinary.com')) return url;

    try {
      const uploadMatch = url.match(/(upload\/v\d+\/)/);
      if (!uploadMatch) return url;

      const uploadPart = uploadMatch[0];
      const afterUpload = url.split(uploadPart)[1];

      const cleanPath = afterUpload
        .split('/')
        .filter(part => {
          return !part.match(/^(w_|h_|c_|f_|q_|dpr_|g_|e_|o_|ar_|fl_)/);
        })
        .join('/');

      const baseUrl = url.split(uploadPart)[0];
      return `${baseUrl}${uploadPart}${cleanPath}`;
    } catch {
      return url;
    }
  };

  const getThumbnailAspectRatio = project => {
    if (project.images && project.images.length > 0) {
      const thumbnailUrl = project.thumbnailUrl || project.images[0]?.url;

      if (thumbnailUrl) {
        const matchedImage = project.images.find(
          img =>
            img.url === thumbnailUrl ||
            (img.publicId && thumbnailUrl.includes(img.publicId)),
        );

        if (matchedImage) {
          if (matchedImage.ratio) {
            return matchedImage.ratio;
          }
          if (matchedImage.width && matchedImage.height) {
            return matchedImage.width / matchedImage.height;
          }
        }
      }

      const firstImage = project.images[0];
      if (firstImage) {
        if (firstImage.ratio) return firstImage.ratio;
        if (firstImage.width && firstImage.height) {
          return firstImage.width / firstImage.height;
        }
      }
    }

    if (project.ratio) {
      return project.ratio;
    }

    return 4 / 3;
  };

  const getThumbnailUrl = project => {
    let url = project.thumbnailUrl;
    if (!url && project.images && project.images.length > 0) {
      url = project.images[0].url;
    }
    if (!url) {
      url = project.imageUrl || '';
    }

    return getCleanCloudinaryUrl(url);
  };

  const getImageCount = project => {
    return project.images?.length || (project.imageUrl ? 1 : 0);
  };

  const getTypeIcon = type => {
    switch (type) {
      case 'project':
        return '📁';
      case 'random':
        return '🎲';
      default:
        return '📄';
    }
  };

  // Handle project click - track and navigate
  const handleProjectClick = async project => {
    try {
      await trackClick(project.id);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  // Get current category label
  const getCurrentCategoryLabel = () => {
    const found = categories.find(c => c.id === selectedCategory);
    return found ? found.name : 'All Work';
  };

  const getCurrentTypeLabel = () => {
    const found = types.find(t => t.id === selectedType);
    return found ? found.name : 'All Types';
  };

  // Handle dropdown selection
  const handleCategorySelect = id => {
    setSelectedCategory(id);
    setIsFilterOpen(false);
    setActiveDropdown(null);
  };

  const handleTypeSelect = id => {
    setSelectedType(id);
    setIsFilterOpen(false);
    setActiveDropdown(null);
  };

  // Toggle dropdown
  const toggleDropdown = dropdown => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
      setIsFilterOpen(false);
    } else {
      setActiveDropdown(dropdown);
      setIsFilterOpen(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {metaData?.metaTitle ||
            'Portfolio - WD Hajbul | Creative Design Showcase'}
        </title>
        <meta
          name="description"
          content={
            metaData?.metaDescription ||
            'Explore my creative portfolio featuring branding, product design, and digital experiences.'
          }
        />
        <meta
          name="keywords"
          content="portfolio, design portfolio, branding, product design, digital design, creative portfolio, UI/UX, graphic design"
        />
        <link
          rel="canonical"
          href={metaData?.canonical || window.location.href}
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={metaData?.og?.url || window.location.href}
        />
        <meta
          property="og:title"
          content={
            metaData?.og?.title ||
            'Portfolio - WD Hajbul | Creative Design Showcase'
          }
        />
        <meta
          property="og:description"
          content={
            metaData?.og?.description ||
            'Explore my creative portfolio featuring branding, product design, and digital experiences.'
          }
        />
        <meta property="og:image" content={metaData?.og?.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta
          property="og:site_name"
          content={metaData?.og?.siteName || 'WD Hajbul Creative Studio'}
        />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:url"
          content={metaData?.og?.url || window.location.href}
        />
        <meta
          name="twitter:title"
          content={
            metaData?.twitter?.title ||
            'Portfolio - WD Hajbul | Creative Design Showcase'
          }
        />
        <meta
          name="twitter:description"
          content={
            metaData?.twitter?.description ||
            'Explore my creative portfolio featuring branding, product design, and digital experiences.'
          }
        />
        <meta name="twitter:image" content={metaData?.twitter?.image} />
        <meta name="twitter:site" content="@wdhejbul" />
        <meta name="twitter:creator" content="@wdhejbul" />

        <meta name="robots" content="index, follow" />

        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'WD Hajbul Portfolio',
            url: window.location.origin,
            description:
              'Creative portfolio of WD Hajbul - Brand Design, Product Design, and Digital Experiences.',
          })}
        </script>
        {schemaMarkup && (
          <script type="application/ld+json">
            {JSON.stringify(schemaMarkup)}
          </script>
        )}
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* Hero Section */}
        <section className="pt-32 px-4 max-w-7xl mx-auto max-sm:p-5 max-sm:pt-24">
          <PortfolioHero totalProjectsCount={totalProjectsCount} />
        </section>

        {/* Stats Section - Using StatsSection component */}
        <StatsSection
          fetchProjectCount={true}
          columns={4}
          variant="default"
          showBorder={true}
        />

        {/* Glassy Divider */}
        <GlassyDivider />

        {/* Filter Section - Animated Dropdown with Full Opacity */}
        <div
          className="sticky top-20 z-20 py-4 px-4"
          style={{
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between gap-4">
              {/* Filter Dropdown Container */}
              <div
                className="flex items-center gap-3 flex-wrap"
                ref={dropdownRef}
              >
                {/* Category Dropdown - Full opacity background */}
                <div className="relative">
                  <button
                    onClick={() => toggleDropdown('category')}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-300 hover:opacity-80"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <FiFilter className="w-3.5 h-3.5" />
                    <span>{getCurrentCategoryLabel()}</span>
                    <FiChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === 'category' ? 'rotate-180' : ''}`}
                    />
                  </button>

                  <AnimatePresence>
                    {activeDropdown === 'category' && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 min-w-[220px] rounded-2xl shadow-xl overflow-hidden z-50"
                        style={{
                          backgroundColor: 'var(--bg)',
                          border: '1px solid var(--border)',
                          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <div className="p-1.5">
                          {categories.map(category => (
                            <button
                              key={category.id}
                              onClick={() => handleCategorySelect(category.id)}
                              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                                selectedCategory === category.id
                                  ? 'bg-black/10 dark:bg-white/10'
                                  : 'hover:bg-black/5 dark:hover:bg-white/5'
                              }`}
                              style={{
                                color:
                                  selectedCategory === category.id
                                    ? 'var(--text)'
                                    : 'var(--muted)',
                              }}
                            >
                              <span>{category.name}</span>
                              <span className="text-xs opacity-60">
                                ({category.count})
                              </span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Type Dropdown - Full opacity background */}
                <div className="relative">
                  <button
                    onClick={() => toggleDropdown('type')}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-300 hover:opacity-80"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <FiGrid className="w-3.5 h-3.5" />
                    <span>{getCurrentTypeLabel()}</span>
                    <FiChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === 'type' ? 'rotate-180' : ''}`}
                    />
                  </button>

                  <AnimatePresence>
                    {activeDropdown === 'type' && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 min-w-[200px] rounded-2xl shadow-xl overflow-hidden z-50"
                        style={{
                          backgroundColor: 'var(--bg)',
                          border: '1px solid var(--border)',
                          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <div className="p-1.5">
                          {types.map(type => (
                            <button
                              key={type.id}
                              onClick={() => handleTypeSelect(type.id)}
                              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                                selectedType === type.id
                                  ? 'bg-black/10 dark:bg-white/10'
                                  : 'hover:bg-black/5 dark:hover:bg-white/5'
                              }`}
                              style={{
                                color:
                                  selectedType === type.id
                                    ? 'var(--text)'
                                    : 'var(--muted)',
                              }}
                            >
                              <span>{type.name}</span>
                              <span className="text-xs opacity-60">
                                ({type.count})
                              </span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Clear Filters */}
                {(selectedCategory !== 'all' || selectedType !== 'all') && (
                  <button
                    onClick={() => {
                      setSelectedCategory('all');
                      setSelectedType('all');
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs transition-all duration-300 hover:opacity-70"
                    style={{
                      color: 'var(--muted)',
                      border: '1px solid var(--border)',
                      backgroundColor: 'var(--border)',
                    }}
                  >
                    <FiX className="w-3 h-3" />
                    Clear Filters
                  </button>
                )}

                <Link
                  to="/illustrations"
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-300 hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <FiPenTool className="w-3.5 h-3.5" />
                  <span>Illustrations</span>
                </Link>
              </div>

              {/* Results Count */}
              <div
                className="text-xs hidden sm:block px-3 py-1 rounded-full"
                style={{
                  color: 'var(--muted)',
                  backgroundColor: 'var(--border)',
                  border: '1px solid var(--border)',
                }}
              >
                {projects?.length || 0} projects
                {hasMore && ' • Scroll for more'}
              </div>
            </div>
          </div>
        </div>

        {/* Google Ad Space - Top Banner - Glassy */}
        <div className="max-w-7xl mx-auto px-4 py-4">
          <GlassyCard className="w-full rounded-2xl min-h-[60px] flex items-center justify-center border border-dashed opacity-50">
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              <span className="opacity-50">📢 Google Ad Space - Top</span>
            </span>
          </GlassyCard>
        </div>

        {/* Masonry Gallery Grid */}
        <section className="py-8 px-4 max-w-7xl mx-auto">
          {loading ? (
            <PortfolioSkeleton />
          ) : error ? (
            <div className="text-center py-20">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiAlertCircle
                  className="w-10 h-10"
                  style={{ color: 'var(--muted)' }}
                />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Unable to load projects
              </h3>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                {error}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-6 py-2 rounded-full text-sm"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                Try Again
              </button>
            </div>
          ) : !projects || projects.length === 0 ? (
            <div className="text-center py-20">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiGrid
                  className="w-10 h-10"
                  style={{ color: 'var(--muted)' }}
                />
              </div>
              <h3 className="text-xl font-semibold mb-2">No projects yet</h3>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Check back soon for amazing work!
              </p>
            </div>
          ) : (
            <>
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="columns-1 sm:columns-2 lg:columns-3 gap-6 [&>div]:mb-6"
              >
                {projects.map((project, index) => {
                  const aspectRatio = getThumbnailAspectRatio(project);
                  const isHovered = hoveredId === project.id;
                  const thumbnailUrl = getThumbnailUrl(project);
                  const imageCount = getImageCount(project);
                  const typeIcon = getTypeIcon(project.type);
                  const imageKey = `${project.id}_0`;

                  return (
                    <motion.div
                      key={project.id}
                      variants={fadeInUp}
                      transition={{ delay: index * 0.05 }}
                      className="break-inside-avoid group relative cursor-pointer rounded-xl overflow-hidden"
                      onMouseEnter={() => setHoveredId(project.id)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      <Link
                        to={`/portfolio/${project.id}`}
                        onClick={() => handleProjectClick(project)}
                        className="block"
                      >
                        <div
                          className="relative overflow-hidden w-full"
                          style={{
                            backgroundColor: 'transparent',
                            aspectRatio: aspectRatio,
                          }}
                        >
                          {!imagesLoaded[imageKey] &&
                            !imageErrors[imageKey] && (
                              <div className="absolute inset-0 flex items-center justify-center z-10">
                                <div
                                  className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
                                  style={{ borderColor: 'var(--text)' }}
                                />
                              </div>
                            )}

                          <img
                            src={
                              imageErrors[imageKey]
                                ? 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E'
                                : thumbnailUrl
                            }
                            alt={`${project.seoTitle || project.title} - ${project.category} ${project.type} showcase`}
                            className="w-full h-full transition-transform duration-700 group-hover:scale-105"
                            style={{
                              objectFit: 'cover',
                              display: 'block',
                              opacity: imagesLoaded[imageKey] ? 1 : 0,
                              width: '100%',
                              height: '100%',
                            }}
                            loading="lazy"
                            onLoad={() => handleImageLoad(project.id, 0)}
                            onError={() => handleImageError(project.id, 0)}
                          />

                          {/* Image Count Badge - Glassy */}
                          {imageCount > 1 && (
                            <div
                              className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-md backdrop-blur-md text-xs z-10"
                              style={{
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                border: '1px solid rgba(255,255,255,0.06)',
                              }}
                            >
                              <FiImage className="w-3 h-3" />
                              <span>{imageCount}</span>
                            </div>
                          )}

                          {/* Type Badge - Glassy */}
                          <div
                            className="absolute top-3 left-3 px-2 py-0.5 rounded-md text-xs font-medium backdrop-blur-md capitalize z-10"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.06)',
                            }}
                          >
                            {typeIcon} {project.type || 'project'}
                          </div>

                          {/* Category Badge - Glassy */}
                          <div
                            className="absolute top-12 left-3 px-2 py-0.5 rounded-md text-xs font-medium backdrop-blur-md capitalize z-10"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.06)',
                            }}
                          >
                            {project.category}
                          </div>

                          {/* Like Count Badge - Glassy */}
                          <div
                            className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full backdrop-blur-md text-xs z-10"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.06)',
                            }}
                          >
                            <FiHeart className="w-2.5 h-2.5" />
                            <span>{project.likes || 0}</span>
                          </div>

                          {/* Hover Overlay - Glassy */}
                          <div
                            className={`absolute inset-0 transition-all duration-500 z-10 ${
                              isHovered ? 'opacity-100' : 'opacity-0'
                            }`}
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.75)',
                              backdropFilter: 'blur(8px)',
                            }}
                          >
                            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                              <div className="transform transition-all duration-500 translate-y-4 group-hover:translate-y-0">
                                <h3 className="text-white text-xl font-bold mb-2 line-clamp-2">
                                  {project.title}
                                </h3>
                                <p className="text-white/80 text-sm mb-4 line-clamp-3">
                                  {project.description}
                                </p>
                                <div className="flex items-center justify-center gap-2 flex-wrap">
                                  <span className="text-xs text-white/60 capitalize">
                                    {project.category}
                                  </span>
                                  <span className="text-white/60">•</span>
                                  <span className="text-xs text-white/60">
                                    {new Date(project.createdAt).getFullYear()}
                                  </span>
                                  {imageCount > 1 && (
                                    <>
                                      <span className="text-white/60">•</span>
                                      <span className="text-xs text-white/60 flex items-center gap-1">
                                        <FiImage className="w-3 h-3" />
                                        {imageCount}
                                      </span>
                                    </>
                                  )}
                                </div>
                                <span className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white text-black hover:bg-white/90 transition-colors">
                                  View Project{' '}
                                  <FiArrowRight className="w-3 h-3" />
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </motion.div>

              {hasMore && (
                <div className="text-center mt-12">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={loadMoreProjects}
                    disabled={loadingMore}
                    className="px-6 py-3 rounded-full text-sm transition-all duration-300"
                    style={{
                      backgroundColor: 'var(--border)',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                          style={{ borderColor: 'var(--text)' }}
                        />
                        Loading...
                      </span>
                    ) : (
                      'Load More'
                    )}
                  </motion.button>
                </div>
              )}
            </>
          )}
        </section>

        {/* Google Ad Space - Middle Banner - Glassy */}
        <div className="max-w-7xl mx-auto px-4 py-4">
          <GlassyCard className="w-full rounded-2xl min-h-[90px] flex items-center justify-center border border-dashed opacity-50">
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              <span className="opacity-50">📢 Google Ad Space - Middle</span>
            </span>
          </GlassyCard>
        </div>

        <ReviewsSection />

        {/* Glassy Divider */}
        <GlassyDivider />

        {/* Fiverr Navigation Card - Ultra Modern with Soft Gradient */}
        <section className="py-8 px-4 max-w-7xl mx-auto">
          <FiverrCard variant="standard" />
        </section>

        {/* Google Ad Space - Bottom Banner - Glassy */}
        <div className="max-w-7xl mx-auto px-4 pb-4">
          <GlassyCard className="w-full rounded-2xl min-h-[60px] flex items-center justify-center border border-dashed opacity-50">
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              <span className="opacity-50">📢 Google Ad Space - Bottom</span>
            </span>
          </GlassyCard>
        </div>

        {/* CTA Section - Glassy */}
        <section className="py-16 px-4">
          <GlassyCard className="max-w-4xl mx-auto text-center p-12 rounded-3xl relative overflow-hidden">
            {/* Animated background glow */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                opacity: 0.05,
              }}
            />

            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Have a project in mind?
              </h2>
              <p
                className="mb-6 max-w-md mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                Let's collaborate and create something extraordinary together.
              </p>
              <motion.a
                whileHover={{ scale: 1.03, gap: '0.75rem' }}
                whileTap={{ scale: 0.97 }}
                href="/hire"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm transition-all duration-300"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                Start a Project
                <FiArrowRight className="w-4 h-4" />
              </motion.a>
            </div>
          </GlassyCard>
        </section>
      </div>

      {/* Add shimmer animation to global styles */}
      <style>{`
  @keyframes shimmer {
    0% {
      transform: translateX(-100%) rotate(12deg);
    }
    100% {
      transform: translateX(200%) rotate(12deg);
    }
  }
`}</style>
    </>
  );
}

export default Portfolio;
